import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import OutlinedInput from '@mui/material/OutlinedInput';
import Skeleton from '@mui/material/Skeleton';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useAtomValue } from 'jotai';
import { useMemo, useState } from 'react';

import { useShowBillingFlow } from '#components/feature-flag/use-show-billing-flow.js';
import { abilityOverrideAtom } from '#src/state/atoms/ability-override-atom.js';
import { useTRPC } from '#src/trpc.js';
import { Can } from '#state/contexts/ability-context.js';
import { DemoProjectsCloneControl } from './demo-projects-clone-control.js';
import { ProjectTriageGroup } from './project-triage-group.js';
import { partitionProjects } from './project-triage-utils.js';

import type { HilProjectPreview } from '@cadstrom/backend/shared';
import type { TriageGroupId } from './project-triage-utils.js';

export interface ProjectTriageListProps {
	projects: HilProjectPreview[];
	isLoading?: boolean;
	orgName: string;
}

/**
 * The projects home, reframed as a triage list: designs waiting on the user are
 * pulled into a highlighted "Needs you" section at the top, so the single next
 * action is always obvious; everything else falls into "All projects", sorted
 * most-recently-updated first.
 */
export function ProjectTriageList({ projects, isLoading = false, orgName }: ProjectTriageListProps) {
	const navigate = useNavigate();
	const [query, setQuery] = useState('');
	const [collapsed, setCollapsed] = useState<Record<TriageGroupId, boolean>>({
		needs: false,
		all: false,
		demo: false,
	});

	const showBillingFlow = useShowBillingFlow(orgName);
	const trpc = useTRPC();
	const { data: currentUser } = useQuery(trpc.user.getCurrent.queryOptions());
	// "View as user" leaves the session untouched, so the flag alone would keep the staff
	// grouping while previewing the customer view.
	const abilityOverride = useAtomValue(abilityOverrideAtom);
	const isStaff = (currentUser?.auth.isStaff ?? false) && abilityOverride.rules === null;
	const partition = useMemo(
		() => partitionProjects(projects, showBillingFlow, isStaff),
		[projects, showBillingFlow, isStaff],
	);

	const trimmedQuery = query.trim().toLowerCase();
	const isSearching = trimmedQuery.length > 0;

	const visible = useMemo(() => {
		if (!isSearching) {
			return partition;
		}
		const matches = (name: string) => name.toLowerCase().includes(trimmedQuery);
		return {
			needs: partition.needs.filter((item) => matches(item.name)),
			all: partition.all.filter((item) => matches(item.name)),
			demo: partition.demo.filter((item) => matches(item.name)),
		};
	}, [partition, isSearching, trimmedQuery]);

	const noMatches = isSearching && visible.needs.length + visible.all.length + visible.demo.length === 0;

	const needsCount = partition.needs.length;
	const totalCount = partition.needs.length + partition.all.length + partition.demo.length;

	// Collapsing is disabled while searching (every group is force-expanded), so ignore
	// toggles then — otherwise hidden collapse state drifts and surfaces when search clears.
	const toggle = (group: TriageGroupId) => {
		if (isSearching) {
			return;
		}
		setCollapsed((prev) => ({ ...prev, [group]: !prev[group] }));
	};
	const isCollapsed = (group: TriageGroupId) => (isSearching ? false : collapsed[group]);

	const showAll = visible.all.length > 0;
	const showDemo = visible.demo.length > 0;

	return (
		<div className='flex min-h-0 grow flex-col gap-6 py-2'>
			<header className='flex flex-wrap items-end justify-between gap-4'>
				<div className='flex flex-col gap-3'>
					<h1 className='text-5xl leading-[1.3] font-semibold tracking-[-0.01em] text-gray-a12'>Projects</h1>
					<p className='text-gray-a11'>
						{needsCount > 0 && (
							<>
								<span className='font-semibold text-amber-a11'>
									{needsCount} need{needsCount === 1 ? 's' : ''} review
								</span>
								{' · '}
							</>
						)}
						{totalCount} total
					</p>
				</div>
				<div className='flex items-center gap-3'>
					<OutlinedInput
						value={query}
						onChange={(event) => setQuery(event.target.value)}
						placeholder='Search projects…'
						size='small'
						className='w-56'
						startAdornment={
							<InputAdornment position='start'>
								<SearchIcon fontSize='small' className='text-gray-a9' />
							</InputAdornment>
						}
						slotProps={{ input: { 'aria-label': 'Search projects' } }}
					/>
					<Can I='manage' a='project'>
						<Button
							variant='contained'
							startIcon={<AddIcon />}
							onClick={() => void navigate({ to: '/orgs/$orgName/projects/new/upload', params: { orgName } })}
						>
							<span>Create project</span>
						</Button>
					</Can>
				</div>
			</header>

			<div className='flex min-h-0 grow flex-col gap-6'>
				{isLoading && projects.length === 0 ?
					<div className='divide-y divide-gray-a4 overflow-hidden rounded-lg border border-gray-a6 bg-gray-2'>
						{[0, 1, 2, 3].map((index) => (
							<div key={index} className='flex items-center justify-between gap-4 px-3.5 py-3'>
								<div className='flex w-full flex-col gap-1'>
									<Skeleton variant='text' width='30%' height={20} />
									<Skeleton variant='text' width='18%' height={14} />
								</div>
								<Skeleton variant='rounded' width={110} height={30} />
							</div>
						))}
					</div>
				: noMatches ?
					<p className='py-10 text-center text-gray-a10'>No projects match your search.</p>
				:	<>
						{(!isSearching || visible.needs.length > 0) && (
							<ProjectTriageGroup
								title='Needs you'
								items={visible.needs}
								orgName={orgName}
								accent
								caughtUp={!isSearching}
								collapsed={isCollapsed('needs')}
								onToggleCollapsed={() => toggle('needs')}
								className={
									isCollapsed('needs') ? 'shrink-0'
									: showAll || showDemo ?
										'max-h-[45%] shrink-0'
									:	'min-h-0 flex-1'
								}
							/>
						)}
						{showAll && (
							<ProjectTriageGroup
								title='All projects'
								items={visible.all}
								orgName={orgName}
								dimmed
								collapsed={isCollapsed('all')}
								onToggleCollapsed={() => toggle('all')}
								className={isCollapsed('all') ? 'shrink-0' : 'min-h-0 flex-1'}
							/>
						)}
						{showDemo && (
							<ProjectTriageGroup
								title='Demo projects'
								items={visible.demo}
								orgName={orgName}
								dimmed
								collapsed={isCollapsed('demo')}
								onToggleCollapsed={() => toggle('demo')}
								className={
									isCollapsed('demo') ? 'shrink-0'
									: showAll ?
										'max-h-[45%] shrink-0'
									:	'min-h-0 flex-1'
								}
							/>
						)}
					</>
				}
			</div>

			{!isSearching && <DemoProjectsCloneControl orgName={orgName} projects={projects} />}
		</div>
	);
}
